import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRateLimit, loginRateLimiter } from '../utils/rateLimiting';
import Layout from '../components/layout/Layout';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, currentUser, userDetails } = useAuth();
  const { isAllowed: isLoginAllowed, getResetTime } = useRateLimit(loginRateLimiter);

  // Check if user is already an admin
  useEffect(() => {
    if (currentUser && userDetails?.role === 'admin') {
      navigate('/admin-panel');
    }
  }, [currentUser, userDetails?.role, navigate]);
  
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    // Check rate limit
    if (!isLoginAllowed()) {
      const resetTime = new Date(getResetTime());
      return setError(`Too many login attempts. Please try again after ${resetTime.toLocaleTimeString()}`);
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Try to login with Firebase Auth
      await login(email, password);
      
      // Check if user is admin after login
      // This will be handled by the useEffect above
      
    } catch (error) {
      console.error('Admin login error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No admin account found with this email');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Login failed: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Layout>
      <div className="flex min-h-[80vh] items-center justify-center py-12 px-4">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Admin Access
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your admin password to continue
            </p>
          </div>
          
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
              <p>{error}</p>
            </div>
          )}
          
          <form className="mt-8 space-y-6" onSubmit={handleAdminLogin}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Admin Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="admin@ucf.edu"
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Sign in as Admin'}
              </button>
            </div>
            
            <div className="text-sm text-center">
              <p className="text-gray-600">
                This area is restricted to administrators only
              </p>
              <p className="text-gray-500 mt-2">
                Contact system administrator if you need access
              </p>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AdminLogin;
